import { toSearchParams } from "@clipboard-health/json-api";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";

// FIXME: Automatically disabled during ESLint config update
const accessWindowQueryParams = z
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  .object({
    filter: z
      .object({
        endAt: z
          .object({
            gt: z.coerce.date(),
          })
          .optional(),
      })
      .optional(),
    page: z
      .object({
        size: z.coerce.number().optional(),
      })
      .optional(),
    include: z.enum(["workers"]).optional(),
  })
  .optional();

export const accessWindowWorker = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
});

export const accessWindow = z.object({
  type: z.literal(API_TYPES.accessWindow),
  id: z.string(),
  attributes: z.object({
    windowName: z.string(),
    endAt: z.string().datetime(),
  }),
  relationships: z
    .object({
      workers: z
        .object({
          type: z.literal(API_TYPES.worker),
          id: z.string(),
        })
        .array(),
    })
    .optional(),
});

export const accessWindows = z.object({
  data: accessWindow.array(),
  included: z.array(accessWindowWorker).optional(),
});

export type AccessWindowDto = z.infer<typeof accessWindow>;
export type AccessWindowsResponse = z.infer<typeof accessWindows>;

export function getAccessWindowsByShiftUrl(shiftId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/shifts/${shiftId}/access-windows`;
}

export function useGetAccessWindowsByShift(
  params: { shiftId: string; query?: z.infer<typeof accessWindowQueryParams> },
  options?: UseGetQueryOptions<AccessWindowsResponse>
): UseQueryResult<AccessWindowsResponse> {
  const { shiftId, query } = params;

  // Memoize the default date to prevent it from changing on every render
  const defaultEndAtDate = useMemo(() => new Date(), []);

  const queryParams = useMemo(
    () => ({
      ...query,
      filter: {
        ...query?.filter,
        endAt: {
          gt: query?.filter?.endAt?.gt ?? defaultEndAtDate,
        },
      },
      include: ["workers"],
    }),
    [query, defaultEndAtDate]
  );

  const stringifiedQuery = `?${toSearchParams(queryParams)}`;

  return useGetQuery({
    url: `${getAccessWindowsByShiftUrl(shiftId)}${stringifiedQuery}`,
    responseSchema: accessWindows,
    ...options,
    enabled: isDefined(shiftId) && options?.enabled,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_ACCESS_WINDOW_FAILURE,
    },
  });
}
